/* Blije Wolkje: one mobile placement owner. Zepto owns rendering and validation.
 * Geometry is committed once per settled viewport. Browser-native scrolling
 * and transform/opacity effects replace per-frame top/scroll write loops. */
(() => {
  const VERSION = 'v11-stable-composition-1.3-continuous-paint';
  if (window.BWZeptoComposition) { window.BWZeptoComposition.mount(); return; }
  const mobile = matchMedia('(max-width: 749px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const textSelector = 'input.pplr_text:not([type="hidden"]), textarea.pplr_text, input.pplr_monogram:not([type="hidden"])';
  const optionsSelector = '.pplr-font-select, .pplr-color-select';
  const sizeSelector = '.sourceBiggerFont, .sourceSmallerFont';
  const sizeZoneSelector = '.textBiggerSmaller';
  const interactiveSelector = `${textSelector}, select:not(.pplr_aux), .pplr-selecter-selected, .pplr-selecter-item, .pplr-swatch-element, .pplrColor, ${sizeSelector}`;
  let current;

  function mount() {
    const product = document.querySelector('product-info[id^="MainProduct-"]');
    if (current?.product === product && product?.isConnected && !current.disposed) return;
    const previous = current;
    current?.cleanup();
    if (!product) return;
    if (window.MobileProductController !== previous) window.MobileProductController?.cleanup?.();
    current = createController(product);
    window.MobileProductController = current;
  }

  function createController(product) {
    const abort = new AbortController();
    const passive = { passive: true, signal: abort.signal };
    const capture = { capture: true, ...passive };
    const media = product.querySelector('.product__media-wrapper');
    const infoColumns = [...product.querySelectorAll('.product__info-wrapper')]
      .filter(element => !element.parentElement.closest('.product__info-wrapper'));
    let active = null, group = null, kind = '', phase = 'idle', revision = 0;
    let pending = 0, pendingReason = '', watch = 0, motion = null, menuEffect = null;
    let keyboardSeen = false, fullHeight = window.visualViewport?.height || innerHeight, fullWidth = innerWidth;
    let manual = false, pointer = null, resizeSizes = new WeakMap();
    let viewportKey = '', lastPlacement = null, correctionKey = '', corrections = 0;
    let panHeader = 0, panValue = null;
    let disposed = false, holdHeader = false;
    let vendorDockOwned = false, vendorDockHadValue = false, vendorDockValue;
    let stickyBounds = null;
    function syncVendorDock() {
      if (mobile.matches && media?.closest('.product__sticky-container') &&
          getComputedStyle(media).position === 'sticky' && product.querySelector('.product-personalizer')) {
        document.querySelector('meta#pplr_meta[name="viewport"]')?.remove();
        if (vendorDockOwned) return;
        vendorDockHadValue = Object.hasOwn(window, 'pplrStickyLive');
        vendorDockValue = window.pplrStickyLive;
        vendorDockOwned = true;
        // Zepto exposes this opt-out. Its live dock otherwise moves the original
        // canvas into .pplrabs, which the theme hides. A zero-width render can
        // then invoke vendor sizing against body and stretch every section.
        window.pplrStickyLive = false;
        window.pplr_sticky_undock?.();
      } else restoreVendorDock();
    }
    function restoreVendorDock() {
      if (!vendorDockOwned) return;
      if (window.pplrStickyLive === false) {
        if (vendorDockHadValue) window.pplrStickyLive = vendorDockValue;
        else delete window.pplrStickyLive;
      }
      vendorDockOwned = false;
    }
    syncVendorDock();
    const owner = {
      version: VERSION, product, state: 'NORMAL', activeInput: null,
      get fieldKind() { return kind; },
      get isDismissing() { return phase === 'returning'; },
      get hasPlacement() { return !!lastPlacement; },
      get isPositioning() { return !!pending || !!motion || phase === 'opening' || phase === 'returning'; },
      get suspendHeader() { return holdHeader && mobile.matches; },
      get disposed() { return disposed; },
      cleanup, exitEditing: release
    };
    const appReady = new MutationObserver(() => {
      if (product.querySelector('.product-personalizer')) { syncVendorDock(); appReady.disconnect(); }
    });
    if (!product.querySelector('.product-personalizer')) appReady.observe(product, { childList: true, subtree: true });
    const resize = new ResizeObserver(entries => {
      let changed = false;
      for (const entry of entries) {
        const size = `${Math.round(entry.contentRect.width)}:${Math.round(entry.contentRect.height)}`;
        const previous = resizeSizes.get(entry.target);
        resizeSizes.set(entry.target, size);
        if (previous && previous !== size) changed = true;
      }
      if (changed && group && mobile.matches) geometryChanged('resize');
    });
    if (media) resize.observe(media);

    function viewport() {
      const vv = window.visualViewport;
      return { top: vv?.offsetTop || 0, height: vv?.height || innerHeight,
        width: vv?.width || innerWidth, scale: vv?.scale || 1 };
    }
    function keyboardOpen(v = viewport()) { return fullHeight - v.height > Math.max(120, fullHeight * .18); }
    function key(v = viewport()) { return [v.top, v.height, v.width, v.scale].map(n => Math.round(n * 10)).join(':'); }
    function headerHeight() {
      if (document.body.classList.contains('bw-zepto-header-enabled')) return 0;
      const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--visible-sticky-header-height'));
      if (Number.isFinite(value)) return Math.max(0, value);
      const header = document.querySelector('.section-header');
      const r = header?.getBoundingClientRect();
      return r ? Math.max(0, r.bottom) : 0;
    }
    function emit(phase) { document.dispatchEvent(new CustomEvent('bw:zepto-composition', { detail: { phase } })); }
    function updateAtc() { window.evaluateStickyAtcVisibility?.(); }
    function isText(el) { return !!el?.matches?.(textSelector) && !el.classList.contains('pplr_aux'); }
    function rootFor(el) { const root = el?.closest?.('.product-personalizer'); return root && product.contains(root) ? root : null; }
    function optionGroup(el) { return el?.closest?.(optionsSelector); }
    function setKeyboardClass() { document.body.classList.toggle('zepto-keyboard-open', kind === 'text' && keyboardSeen && phase !== 'returning'); }

    function cancelMotion(stopScroll = true) {
      cancelAnimationFrame(watch); watch = 0;
      motion?.effects?.forEach(effect => effect.cancel());
      if (motion && motion.type !== 'handoff' && stopScroll) {
        // One cancellation at new customer intent, never a per-frame write.
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }
      motion = null;
    }
    function begin(el, nextKind) {
      if (!mobile.matches || !media?.closest('.product__sticky-container') || getComputedStyle(media).position !== 'sticky' || !rootFor(el)) return;
      syncVendorDock();
      const nextGroup = el.closest('.pplr-wrapper, [data-pplr-wrapper]') || el;
      if (group === nextGroup && kind === nextKind && phase !== 'idle' &&
          !(nextKind === 'text' && phase === 'anchored')) return;
      release(false);
      active = el; group = nextGroup; kind = nextKind; manual = false;
      owner.activeInput = el;
      owner.state = nextKind === 'text' ? 'EDITING' : 'ANCHORED';
      phase = nextKind === 'text' ? 'opening' : 'options';
      const bounds = media.closest('.product__sticky-container').getBoundingClientRect();
      stickyBounds = { start: bounds.top + scrollY, end: bounds.bottom + scrollY - media.offsetHeight };
      const v = viewport();
      keyboardSeen = nextKind === 'text' && keyboardOpen(v);
      viewportKey = key(v);
      correctionKey = ''; corrections = 0;
      // Focus-induced native scrolling must not reset the header mid-opening.
      holdHeader = true;
      document.body.classList.add('bw-zepto-smooth', 'zepto-editing-mode');
      document.body.classList.toggle('zepto-anchored', nextKind !== 'text');
      setKeyboardClass();
      emit('start');
      resize.observe(group);
      updateAtc();
      queue(nextKind === 'text' ? 'focus' : 'option');
    }
    function returning() {
      if (!group || kind !== 'text' || phase === 'returning' || phase === 'anchored') return;
      revision++; cancelAnimationFrame(pending); pending = 0; cancelMotion();
      phase = 'returning'; holdHeader = true;
      document.body.classList.remove('zepto-keyboard-open');
      syncTextPreview();
      emit('dismiss');
      queue('return');
    }
    function queue(reason) {
      // A late scroll notification must not replace a correction before it commits.
      if (pending && pendingReason === 'correction' && (reason === 'late-scroll' || reason === 'native-scroll')) return;
      cancelAnimationFrame(pending); pending = 0;
      pendingReason = reason;
      if (!group || manual || disposed || !mobile.matches) return;
      const token = revision;
      let sample = '', quietSince = performance.now();
      const started = quietSince;
      const settle = now => {
        pending = 0;
        if (token !== revision || !group || manual || disposed) return;
        const v = viewport();
        if (Math.abs(v.scale - 1) > .02) return; // Customer pinch zoom stays native.
        const next = `${key(v)}:${Math.round(scrollY)}`;
        if (next !== sample) { sample = next; quietSince = now; }
        if (now - quietSince < 64) {
          // Bounded, read-only settlement sampling after an actual event.
          if (now - started < 600) pending = requestAnimationFrame(settle);
          return;
        }
        if (phase === 'opening' && !keyboardSeen && reason !== 'hardware' && window.visualViewport) return;
        if (phase === 'returning' && keyboardSeen && keyboardOpen(v)) return;
        if (reason === 'late-scroll') reconcile();
        else place(reason);
      };
      pending = requestAnimationFrame(settle);
    }
    function syncTextPreview() {
      if (!media || kind !== 'text' || !['text', 'returning'].includes(phase) || !lastPlacement || lastPlacement.fits || manual) return;
      // Keep the original full-size sticky photo immediately above the heading.
      // On iOS sticky top is relative to the layout viewport: offsetTop belongs
      // in this paint anchor only, never in the document scroll destination.
      const v = viewport();
      const top = v.top + group.getBoundingClientRect().top - lastPlacement.imageHeight - 8;
      media.style.setProperty('--bw-zepto-media-top', `${top}px`);
      media.style.setProperty('--bw-zepto-pan-y', '0px');
      emit('position');
    }
    function syncPan(v) {
      if (!media || !lastPlacement?.fits || Math.abs(v.scale - 1) > .02) return;
      const pan = Math.max(0, v.top - panHeader);
      if (panValue === pan) return;
      panValue = pan;
      media.style.setProperty('--bw-zepto-pan-y', `${pan}px`);
    }
    function geometryChanged(reason) {
      syncVendorDock();
      const v = viewport(), nextKey = key(v);
      if (motion?.type === 'handoff' && nextKey === viewportKey) return;
      if (Math.abs(innerWidth - fullWidth) > 2) {
        fullWidth = innerWidth;
        fullHeight = Math.max(v.height, innerHeight);
      }
      if (!group) { if (!isText(document.activeElement)) fullHeight = Math.max(fullHeight, v.height); return; }
      if (!mobile.matches) { release(); return; }
      if (reason === 'viewport' && nextKey === viewportKey) return;
      viewportKey = nextKey;
      syncPan(v);
      if (kind === 'text' && keyboardOpen(v)) keyboardSeen = true;
      if (kind === 'text' && keyboardSeen && !keyboardOpen(v) && phase !== 'opening') {
        returning();
      }
      // After the initial insufficient-space text placement, iOS owns panning. Its
      // offsetTop follows native focus panning and our scrollY; feeding that
      // offset back into another scroll target caused a 0 <-> 315px loop.
      if (kind === 'text' && phase === 'text' && keyboardOpen(v) && lastPlacement?.fits === false) { syncTextPreview(); return; }
      cancelMotion();
      if (phase === 'returning') syncTextPreview();
      setKeyboardClass();
      queue(reason);
    }
    function place(reason) {
      if (!active?.isConnected || !group?.isConnected || !media?.isConnected) { release(); return; }
      const v = viewport(), y = scrollY;
      // Batch geometry reads before changing classes/styles or scroll position.
      const g = group.getBoundingClientRect(), input = active.getBoundingClientRect();
      const photo = media.getBoundingClientRect();
      // Snapshot the two existing flow columns, not isolated floating fields.
      // Their inverse translations preserve every neighbouring section's space.
      const handoff = phase === 'returning' && !keyboardOpen(v) && !reduced.matches && infoColumns.length && media.animate;
      const infoBefore = handoff ? infoColumns.map(element => [element, element.getBoundingClientRect()]) : [];
      const visibleHeader = headerHeight();
      const top = Math.max(v.top, visibleHeader);
      const bottom = v.top + v.height - 8;
      const fieldHeight = kind === 'text' ? Math.max(input.height, input.bottom - g.top) : g.height;
      const required = photo.height + 8 + fieldHeight;
      const fits = required <= bottom - top;
      const docTop = g.top + y;
      const nativeTextLayout = kind === 'text' && keyboardOpen(v) && !fits;
      // Use the keyboard's available height for the one text alignment. Its
      // offsetTop changes in response to scrollTo and is not a stable target.
      const sectionTop = fits ? top + photo.height + 8 : nativeTextLayout
        ? Math.max(8, v.height - 8 - fieldHeight)
        : Math.max(v.top + 8, bottom - fieldHeight);
      const maxY = Math.max(0, document.documentElement.scrollHeight - document.documentElement.clientHeight);
      const targetY = Math.max(0, Math.min(maxY, docTop - sectionTop));
      const targetTop = Math.max(top, docTop - targetY - photo.height - 8);
      const finalFieldTop = docTop - targetY;
      const wasOverflow = media.classList.contains('zepto-composition-overflow');
      const already = lastPlacement?.group === group && lastPlacement.fits === fits &&
        Math.abs(lastPlacement.top - targetTop) < 1 && Math.abs(y - targetY) < 1;
      lastPlacement = { group, fits, top: targetTop, targetY, required,
        available: bottom - top, shortfall: Math.max(0, required - (bottom - top)),
        imageHeight: photo.height, fieldHeight, viewportTop: v.top, viewportHeight: v.height };
      if (already) { settled(); return; }
      if (reason === 'correction') corrections++;
      holdHeader = true;
      media.classList.add('zepto-composition-active');
      media.classList.toggle('zepto-composition-overflow', !fits);
      panHeader = visibleHeader;
      panValue = Math.max(0, v.top - visibleHeader);
      const baseTop = targetTop - panValue;
      if (media.style.getPropertyValue('--bw-zepto-media-top') !== `${baseTop}px`) media.style.setProperty('--bw-zepto-media-top', `${baseTop}px`);
      media.style.setProperty('--bw-zepto-pan-y', `${panValue}px`);
      product.style.setProperty('--bw-zepto-options-space', `${Math.max(88, Math.min(320, bottom - (finalFieldTop + g.height) - 8))}px`);
      if (phase === 'opening') phase = 'text';
      setKeyboardClass();
      const keyboardMoving = keyboardSeen && phase !== 'returning';
      // Keyboard placement/corrections commit once after the viewport settles.
      // Options use native smooth scrolling; Done animates the committed flow.
      const safePath = fits && !wasOverflow && targetTop + photo.height <= Math.min(g.top, finalFieldTop) - 4;
      const smooth = !handoff && safePath && !reduced.matches && !keyboardMoving && reason !== 'correction' && Math.abs(y - targetY) > 1;
      if (Math.abs(y - targetY) > 1) {
        if (smooth) {
          motion = { token: revision, targetY, previous: y, quiet: performance.now(), started: performance.now() };
          window.scrollTo({ top: targetY, behavior: 'smooth' });
          watchNativeScroll();
        } else window.scrollTo({ top: targetY, behavior: 'instant' });
      }
      syncTextPreview();
      if (handoff) {
        // Commit document geometry once, then animate only compositor transforms.
        // No scroll/top writes on animation frames, and no input reparenting.
        const effects = [[media, photo], ...infoBefore].flatMap(([element, before]) => {
          const delta = before.top - element.getBoundingClientRect().top;
          return Math.abs(delta) > .5 ? [element.animate([
            { transform: `translate3d(0, ${delta}px, 0)` },
            { transform: 'translate3d(0, 0, 0)' }
          ], { duration: 300, easing: 'cubic-bezier(.25, .1, .25, 1)' })] : [];
        });
        if (effects.length) {
          const started = document.timeline.currentTime;
          effects.forEach(effect => { effect.startTime = started; });
          const token = revision;
          motion = { type: 'handoff', effects };
          Promise.all(effects.map(effect => effect.finished)).then(() => {
            if (disposed || token !== revision || motion?.effects !== effects) return;
            motion = null;
            settled();
          }).catch(() => {});
        }
      }
      emit('position');
      if (!motion) settled();
    }
    function watchNativeScroll() {
      cancelAnimationFrame(watch);
      const token = revision;
      const sample = now => {
        watch = 0;
        if (!motion || token !== revision) return;
        const y = scrollY;
        if (Math.abs(y - motion.previous) > .5) { motion.previous = y; motion.quiet = now; }
        // Only read scrollY here. No style/layout writes in the animation loop.
        if ((now - motion.quiet >= 80 && now - motion.started >= 80) || now - motion.started > 1600) finishScroll();
        else watch = requestAnimationFrame(sample);
      };
      watch = requestAnimationFrame(sample);
    }
    function finishScroll() {
      if (!motion || motion.type === 'handoff') return;
      const target = motion.targetY;
      motion = null; cancelAnimationFrame(watch); watch = 0;
      // Native focus may supersede a scroll. Reconcile once for that geometry,
      // rather than restart another animated scroll and fight the browser.
      if (!manual && Math.abs(scrollY - target) > 2 && correctionKey !== key()) {
        correctionKey = key(); queue('correction'); return;
      }
      settled();
    }
    function settled() {
      if (phase === 'returning') { phase = 'anchored'; owner.state = 'ANCHORED'; keyboardSeen = false; }
      if (phase === 'options' || phase === 'anchored') holdHeader = false;
      document.body.classList.toggle('zepto-anchored', owner.state === 'ANCHORED');
      emit('settled');
      updateAtc();
    }
    function reconcile() {
      if (!group || manual || motion || pending || disposed || kind !== 'text' || phase !== 'text') return;
      // Do not restart positioning from scrollend after the text placement.
      // The unchanged photo cannot fit with this field/keyboard.
      if (lastPlacement?.fits === false && keyboardOpen()) return;
      const v = viewport(), field = active.getBoundingClientRect(), photo = media.getBoundingClientRect();
      const heading = group.getBoundingClientRect();
      const covered = lastPlacement?.fits && heading.top < photo.bottom + 4;
      if (!covered && field.top >= v.top && field.bottom <= v.top + v.height - 8) return;
      const k = key(v);
      if (correctionKey !== k) { correctionKey = k; corrections = 0; }
      if (corrections < 1) queue('correction');
      else {
        // If Safari repeatedly pans despite correction, stop locking the photo.
        // The full-size image remains available by scrolling and input stays live.
        media.classList.add('zepto-composition-overflow');
        manual = true; holdHeader = false;
      }
    }
    function release(removeClass = true) {
      const wasActive = !!group;
      revision++; cancelAnimationFrame(pending); pending = 0; cancelMotion();
      if (group) resize.unobserve(group);
      active = null; group = null; kind = ''; phase = 'idle'; manual = false;
      owner.activeInput = null; owner.state = 'NORMAL'; holdHeader = false;
      lastPlacement = null; keyboardSeen = false;
      media?.classList.remove('zepto-composition-active', 'zepto-composition-overflow');
      media?.style.removeProperty('--bw-zepto-media-top');
      media?.style.removeProperty('--bw-zepto-pan-y');
      panValue = null;
      product.style.removeProperty('--bw-zepto-options-space');
      document.body.classList.remove('zepto-editing-mode', 'zepto-anchored', 'zepto-keyboard-open');
      if (removeClass && !mobile.matches) document.body.classList.remove('bw-zepto-smooth');
      if (wasActive) emit('end');
      updateAtc();
    }
    function cleanup() {
      if (disposed) return;
      release(); disposed = true; abort.abort(); resize.disconnect(); menuEffect?.cancel();
      restoreVendorDock(); appReady.disconnect();
      document.body.classList.remove('bw-zepto-smooth');
    }
    function syncGallery() {
      const mg = product.querySelector('media-gallery');
      const first = mg?.querySelector('.product__media-item:not(.hidden)');
      if (!first) return;
      const firstMedia = first.querySelector('.product__media');
      const preview = product.querySelector('.pplr_preview_wrapper');
      if (firstMedia) {
        product.querySelectorAll('.product__media.pplr').forEach(el => { if (el !== firstMedia) el.classList.remove('pplr'); });
        firstMedia.classList.add('pplr');
        if (preview && preview.parentElement !== firstMedia) firstMedia.append(preview);
        const img = first.querySelector('img');
        if (window.jQuery && img) window.pplr_first_prod_img = window.jQuery(img);
      }
      const slider = mg.querySelector('[id^="Slider-"]');
      const x = first.offsetLeft || 0;
      if (first.classList.contains('is-active') && (!slider || Math.abs(slider.scrollLeft - x) < 2)) return;
      if (!mobile.matches) { mg.setActiveMedia?.(first.dataset.mediaId, false); return; }
      slider?.scrollTo({ left: x, behavior: reduced.matches ? 'instant' : 'smooth' });
      mg.querySelectorAll('.product__media-item').forEach(el => el.classList.toggle('is-active', el === first));
      mg.querySelector('[id^="GalleryViewer"]')?.update?.();
      const thumb = mg.querySelector(`.thumbnail-list__item[data-target="${CSS.escape(first.dataset.mediaId)}"]`);
      if (thumb) mg.setActiveThumbnail?.(thumb);
    }
    function sizeZoneAt(target, point) {
      const direct = target.closest?.(sizeZoneSelector);
      if (direct && rootFor(direct)) return direct;
      const field = target.closest?.('.pplr-wrapper, [data-pplr-wrapper]');
      // Only the size row's nearby empty space; never intercept another field,
      // label, swatch, picker or the editable text's native selection gestures.
      if (!field || !rootFor(field) || target.closest('input, textarea, select, a, button, label, [role="button"], .pplr-selecter-options')) return null;
      const zone = field.querySelector(sizeZoneSelector);
      if (!zone || !point) return null;
      const r = zone.getBoundingClientRect(), slop = 8;
      return point.clientX >= r.left - slop && point.clientX <= r.right + slop &&
        point.clientY >= r.top - slop && point.clientY <= r.bottom + slop ? zone : null;
    }
    function pointerDown(event) {
      const el = event.target;
      if (!rootFor(el)) {
        if (group) { if (kind === 'text' && keyboardSeen) returning(); else release(); }
        return;
      }
      if (el.closest(sizeSelector)) return;
      if (!el.closest(interactiveSelector)) return;
      syncGallery();
      const options = optionGroup(el);
      if (options) { if (!el.closest('.pplr-selecter-options')) begin(el.closest('.pplr-selecter-selected, .pplr-swatch-element, .pplrColor, select') || el, 'options'); }
      else if (isText(el) && document.activeElement === el) begin(el, 'text');
    }
    function focusIn(event) {
      const el = event.target;
      if (!rootFor(el)) {
        if (group) { if (kind === 'text' && keyboardSeen) returning(); else release(); }
        return;
      }
      if (isText(el)) { syncGallery(); begin(el, 'text'); }
      else if (optionGroup(el) && !el.closest('.pplr-selecter-options')) { syncGallery(); begin(el, 'options'); }
    }
    function focusOut(event) {
      if (event.target !== active || kind !== 'text') return;
      const token = revision;
      queueMicrotask(() => {
        if (token !== revision || event.target !== active || document.activeElement === active) return;
        const next = document.activeElement;
        if (optionGroup(next) && rootFor(next)) begin(next, 'options');
        else returning();
      });
    }
    function click(event) {
      const el = event.target;
      if (!rootFor(el)) return;
      if (el.closest(interactiveSelector)) syncGallery();
      // Opacity only; app retains its display, expanded state, focus and events.
      if (el.closest('.pplr-selecter-selected')) queueMicrotask(() => {
        if (disposed || reduced.matches) return;
        const wrapper = optionGroup(el), selected = wrapper?.querySelector('.pplr-selecter-selected');
        const list = wrapper?.querySelector('.pplr-selecter-options');
        if (list && selected?.getAttribute('aria-expanded') === 'true' && list.animate) {
          menuEffect?.cancel(); menuEffect = list.animate([{ opacity: .35 }, { opacity: 1 }], { duration: 180, easing: 'cubic-bezier(.25, .1, .25, 1)' });
        }
      });
    }
    document.addEventListener('pointerdown', pointerDown, capture);
    document.addEventListener('focusin', focusIn, capture);
    document.addEventListener('focusout', focusOut, capture);
    document.addEventListener('click', click, { signal: abort.signal });
    document.addEventListener('input', event => {
      if (!rootFor(event.target)) return;
      // A hardware keyboard may produce no viewport event. Align once on its
      // first edit; ordinary typing never schedules repositioning.
      if (phase === 'opening' && !keyboardSeen) queue('hardware');
    }, capture);
    document.addEventListener('pointerdown', event => {
      if (mobile.matches && rootFor(event.target) && sizeZoneAt(event.target, event) && isText(document.activeElement)) event.preventDefault();
    }, { capture: true, signal: abort.signal });
    document.addEventListener('dblclick', event => {
      if (mobile.matches && rootFor(event.target) && sizeZoneAt(event.target, event)) event.preventDefault();
    }, { signal: abort.signal });
    document.addEventListener('touchstart', event => {
      const t = event.touches[0]; pointer = t && event.touches.length === 1 ? { x: t.clientX, y: t.clientY, target: event.target, zone: sizeZoneAt(event.target, t), moved: false } : null;
    }, capture);
    document.addEventListener('touchend', event => {
      const t = event.changedTouches[0], gesture = pointer;
      pointer = null;
      if (!mobile.matches || !gesture?.zone?.isConnected || gesture.moved ||
          event.touches.length || !t || Math.hypot(t.clientX - gesture.x, t.clientY - gesture.y) > 10) return;
      // A near miss must not smart-zoom the page or blur the text field and
      // restart the keyboard handoff. Empty row/AAA taps do nothing. Only an
      // actual app button gets its unchanged click action, once per tap.
      event.preventDefault();
      const button = gesture.target.closest(sizeSelector);
      if (button && gesture.zone.contains(button)) button.click();
    }, { passive: false, signal: abort.signal });
    document.addEventListener('touchcancel', () => { pointer = null; }, capture);
    document.addEventListener('touchmove', event => {
      if (!pointer || event.touches.length !== 1) return;
      const t = event.touches[0];
      if (Math.hypot(t.clientX - pointer.x, t.clientY - pointer.y) <= 10) return;
      pointer.moved = true;
      if (!group) return;
      if (pointer.target.closest('.pplr-selecter-options')) return;
      if (pointer.target.closest('input, textarea')) { manual = true; return; }
      release();
    }, capture);
    window.addEventListener('wheel', event => { if (group && !event.target.closest('.pplr-selecter-options')) release(); }, passive);
    document.addEventListener('keydown', event => {
      if (group && !rootFor(event.target) && ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown'].includes(event.key)) release();
    }, { signal: abort.signal });
    window.addEventListener('resize', () => geometryChanged('resize'), passive);
    window.addEventListener('pplrAppBuild', syncVendorDock, passive);
    window.visualViewport?.addEventListener('resize', () => geometryChanged('viewport'), passive);
    window.visualViewport?.addEventListener('scroll', () => geometryChanged('viewport'), passive);
    window.addEventListener('scroll', () => {
      if (!group || manual || motion) return;
      if ((phase === 'options' || phase === 'anchored') && stickyBounds &&
          (scrollY < stickyBounds.start - 2 || scrollY >= stickyBounds.end)) { release(); return; }
      if (phase === 'opening' || phase === 'returning') queue('native-scroll');
      else if (phase === 'text') { syncTextPreview(); queue('late-scroll'); }
    }, passive);
    document.addEventListener('scrollend', () => { if (motion) finishScroll(); else reconcile(); }, passive);
    if (mobile.matches) document.body.classList.add('bw-zepto-smooth');
    return owner;
  }
  window.BWZeptoComposition = { mount };
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    if (current && event.target.contains(current.product)) { current.cleanup(); current = null; }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
